import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COMPANY_FLAG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { CompanyFlagCompositeEntity } from '@nk/data-entities-lib';
import { CardComponent, CardContentComponent, CardHeaderComponent, SpinnerComponent } from '@nk/ui-lib';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import * as dayjs from 'dayjs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { FlagEndDatePipe } from '../../pipes/flag-end-date.pipe';
import { CompanyFlagContextPopoverComponent } from '../company-flag-context/company-flag-context-popover.component';
import { CompanyFlagIconComponent } from '../company-flag-icon/company-flag-icon.component';

interface LookbackFrequency {
  key: string;
  label: string;
  lookbackDate: string | null;
  lookbackDays: number | null;
}

@Component({
  selector: 'nk-company-flag-card',
  templateUrl: 'company-flag-card.component.html',
  styles: [
    `
      table td {
        vertical-align: middle;
      }
    `,
  ],
  imports: [
    DatePipe,
    TranslateModule,
    BsDropdownModule,
    CompanyFlagIconComponent,
    CardContentComponent,
    CardHeaderComponent,
    CardComponent,
    SpinnerComponent,
    RouterLink,
    CompanyFlagContextPopoverComponent,
    FlagEndDatePipe,
  ],
})
export class CompanyFlagCardComponent implements OnInit {
  public companyUuid = input.required<string>();
  public printVersion = input(false);
  public simpleLayout = input(true);
  public companyFlagTypeKeys = input<string[] | null>(null);
  public showDetails = input(true);
  public titleOverride = input<string | null>(null);
  public showYearSelector = input(true);

  public companyFlagCompositeList = signal<CompanyFlagCompositeEntity[]>([]);
  public today = Date.now();
  public frequencyList!: LookbackFrequency[];
  public currentFrequency!: LookbackFrequency;
  public loading = signal(true);

  private _destroy = inject(DestroyRef);
  private _companyFlagService = inject(COMPANY_FLAG_API_SERVICE_TOKEN);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, {
    optional: true,
  });
  private _translateService = inject(TranslateService);

  public ngOnInit(): void {
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this.constructor.name, true);
    }
    const pastMonth = dayjs(this.today).subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
    const past3Months = dayjs(this.today).subtract(3, 'months').endOf('month').format('YYYY-MM-DD');

    const thisYear = dayjs(this.today).subtract(1, 'years').endOf('year').format('YYYY-MM-DD');
    // TODO: thisYear and last1Year are identical, this is probably a mistake.
    // Both compute subtract(1, 'years').endOf('year').
    // Looks like thisYear should be dayjs(this.today).startOf('year') instead.
    // const thisYear = dayjs(this.today).startOf('year').format('YYYY-MM-DD');

    const last1Year = dayjs(this.today).subtract(1, 'years').endOf('year').format('YYYY-MM-DD');
    const last3Years = dayjs(this.today).subtract(3, 'years').endOf('year').format('YYYY-MM-DD');
    const last10Years = dayjs(this.today).subtract(10, 'years').endOf('year').format('YYYY-MM-DD');

    this.frequencyList = [
      {
        key: 'default',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.current_flags'),
        ),
        lookbackDate: null,
        lookbackDays: null,
      },
      {
        key: 'past7Days',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.past_7_days'),
        ),
        lookbackDate: null,
        lookbackDays: 7,
      },
      {
        key: 'past14Days',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.past_14_days'),
        ),
        lookbackDate: null,
        lookbackDays: 14,
      },
      {
        key: 'thisMonth',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.this_month'),
        ),
        lookbackDate: pastMonth,
        lookbackDays: null,
      },
      {
        key: 'past3Months',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.past_three_months'),
        ),
        lookbackDate: past3Months,
        lookbackDays: null,
      },
      {
        key: 'thisYear',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.this_year'),
        ),
        lookbackDate: thisYear,
        lookbackDays: null,
      },

      {
        key: 'oneYear',
        label: this._translateService.instant(extractTranslation('company_page.company_flags_card.frequency.one_year')),
        lookbackDate: last1Year,
        lookbackDays: null,
      },
      {
        key: 'last3Years',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.last_three_years'),
        ),
        lookbackDate: last3Years,
        lookbackDays: null,
      },
      {
        key: 'last10Years',
        label: this._translateService.instant(
          extractTranslation('company_page.company_flags_card.frequency.last_ten_years'),
        ),
        lookbackDate: last10Years,
        lookbackDays: null,
      },
    ];

    this.currentFrequency = this.frequencyList.find((f) => f.key === 'default') ?? this.frequencyList[0];
    this.fetchFlags();
  }

  public fetchFlags(): void {
    this.loading.set(true);
    this._companyFlagService.companyFlagCompositeByCompanyCache
      .get(
        this.companyUuid(),
        this.currentFrequency.lookbackDays ?? undefined,
        this.currentFrequency.lookbackDate ?? undefined,
        false,
        this.companyFlagTypeKeys() ?? undefined,
      )
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe({
        next: (payload) => {
          this.loading.set(false);
          this.companyFlagCompositeList.set(
            payload
              .filter((x) => !x.company_flag_type.app_hidden_flag)
              .sort((a, b) => (b.company_flag.flag_date?.valueOf() ?? 0) - (a.company_flag.flag_date?.valueOf() ?? 0)),
          );
          if (this._reportLoadingStateService) {
            this._reportLoadingStateService.setLoadingState(this.constructor.name, false);
          }
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  public changeFrequency(frequency: LookbackFrequency): void {
    this.currentFrequency = frequency;
    this.fetchFlags();
  }
}
